import { lazy, Suspense, useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Route, Routes, useLocation } from 'react-router-dom';
import { apiFetch, setCsrfToken } from '@/lib/api.js';
import Header from '@/components/Header.jsx';
import Footer from '@/components/Footer.jsx';
import Toast from '@/components/Toast.jsx';
import { pageMotion } from '@/lib/motion.js';

const HomePage = lazy(() => import('@/pages/HomePage.jsx'));
const PlayersPage = lazy(() => import('@/pages/PlayersPage.jsx'));
const FeedPage = lazy(() => import('@/pages/FeedPage.jsx'));
const PostDetailPage = lazy(() => import('@/pages/PostDetailPage.jsx'));
const DonatePage = lazy(() => import('@/pages/DonatePage.jsx'));
const DashboardPage = lazy(() => import('@/pages/DashboardPage.jsx'));
const UserProfilePage = lazy(() => import('@/pages/UserProfilePage.jsx'));
const MyProfilePage = lazy(() => import('@/pages/MyProfilePage.jsx'));
const AdminPage = lazy(() => import('@/pages/AdminPage.jsx'));
const MapPage = lazy(() => import('@/pages/MapPage.jsx'));
const LegalPage = lazy(() => import('@/pages/LegalPage.jsx'));
const PaymentSuccessPage = lazy(() => import('@/pages/PaymentSuccessPage.jsx'));

const defaultLinks = {
  wiki: 'https://wiki.kozelcraft.ru',
  map: 'https://map.kozelcraft.ru/',
  shop: 'https://kozel.axogroup.ru/',
  email: 'kozel@gmail.com',
  serverIp: 'kozelcraft.ru'
};

export default function App() {
  const location = useLocation();
  const [me, setMe] = useState(null);
  const [links, setLinks] = useState(defaultLinks);
  const [hcaptcha, setHcaptcha] = useState({ siteKey: '' });
  const [toast, setToast] = useState('');

  const showToast = useCallback((message) => {
    setToast(message);
    window.clearTimeout(window.__kozelToastTimer);
    window.__kozelToastTimer = window.setTimeout(() => setToast(''), 3200);
  }, []);

  const refreshMe = useCallback(async () => {
    try {
      const data = await apiFetch('/api/me');
      setMe(data);
      setCsrfToken(data.csrfToken);
    } catch {
      setMe({ user: null, canPost: false, isAdmin: false });
      setCsrfToken('');
    }
  }, []);

  useEffect(() => {
    refreshMe();
    const rolePoll = window.setInterval(refreshMe, 2500);
    apiFetch('/api/public-config')
      .then((config) => {
        setLinks({ ...defaultLinks, ...config.links });
        setHcaptcha(config.hcaptcha || { siteKey: '' });
      })
      .catch(() => {
        setLinks(defaultLinks);
        setHcaptcha({ siteKey: '' });
      });
    return () => window.clearInterval(rolePoll);
  }, [refreshMe]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location.pathname, location.hash]);

  return (
    <div className="app-shell">
      <Header me={me} links={links} />
      <Suspense
        fallback={
          <main className="page loading-row">
            <md-circular-progress indeterminate />
            Загрузка
          </main>
        }
      >
        <AnimatePresence mode="wait" initial={false}>
          <motion.div className="route-motion" key={location.pathname} {...pageMotion}>
            <Routes location={location}>
              <Route path="/" element={<HomePage links={links} showToast={showToast} />} />
              <Route path="/players" element={<PlayersPage showToast={showToast} />} />
              <Route path="/feed" element={<FeedPage me={me} showToast={showToast} />} />
              <Route path="/posts/:id" element={<PostDetailPage me={me} showToast={showToast} />} />
              <Route
                path="/donate"
                element={
                  <DonatePage
                    me={me}
                    hcaptchaSiteKey={hcaptcha.siteKey}
                    showToast={showToast}
                  />
                }
              />
              <Route
                path="/dashboard"
                element={<DashboardPage me={me} refreshMe={refreshMe} showToast={showToast} />}
              />
              <Route path="/myprofile" element={<MyProfilePage me={me} />} />
              <Route path="/admin" element={<AdminPage me={me} showToast={showToast} />} />
              <Route path="/u/:discordId" element={<UserProfilePage showToast={showToast} />} />
              <Route path="/users/:discordId" element={<UserProfilePage showToast={showToast} />} />
              <Route path="/map" element={<MapPage links={links} />} />
              <Route path="/legal/:slug" element={<LegalPage />} />
              <Route path="/payment/success" element={<PaymentSuccessPage />} />
            </Routes>
          </motion.div>
        </AnimatePresence>
      </Suspense>
      <Footer links={links} />
      <Toast message={toast} />
    </div>
  );
}
